import { baseApi } from './baseApi';

export interface BankNetbankingInfo {
  bankId: string;
  lumpsum: boolean;
  hybrid: boolean;
}

export interface BankEmandateInfo {
  bankId: string;
  netBanking: boolean;
  debitCard: boolean;
  aadhaar: boolean;
  pan: boolean;
}

export interface BankDetailsItem {
  id: number | string;
  name: string;
  ifscCode: string;
  netbanking: BankNetbankingInfo;
  emandate: BankEmandateInfo;
}

export interface CreateBankDetailsRequest {
  gateway: string;
  bankId: string;
  ifsc: string;
  bankName: string;
  remarks: string;
  isActive: boolean;
  lumpsumNetBanking: boolean;
  hybridNetBanking: boolean;
  emandateNetBanking: boolean;
  emandateDebitCard: boolean;
  emandateAadhar: boolean;
  emandatePAN: boolean;
  otmNetBanking: boolean;
  otmDebitCard: boolean;
}

export interface UpdateBankDetailsRequest extends CreateBankDetailsRequest {
  id: string;
}

export interface GetBankDetailsParams {
  gateway?: string;
  ifsc?: string;
  bankName?: string;
  page?: number; // 1-based per spec
  limit?: number;
}

export interface BankDetailsMeta {
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

export interface BankDetailsResponse {
  data: BankDetailsItem[];
  meta?: BankDetailsMeta;
}

export interface BankHistoryParams {
  ifsc?: string;
}

export interface BankHistoryItem {
  id?: string;
  transactionType: string;
  bankId: string;
  updatedDate: string;
  remarks: string | null;
}

export interface BankHistoryResponse {
  success: boolean;
  data: {
    data: BankHistoryItem[];
    meta?: BankDetailsMeta;
  };
}

export interface DownloadExcelRequest {
  gateway: string;
  ifsc?: string;
  bankName?: string;
}

const removeNullOrUndefined = (obj: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== '')
  );

export const bankApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBankDetails: builder.query<BankDetailsResponse, GetBankDetailsParams | void>({
      query: (params) => ({
        url: '/bank-details',
        params: params ? removeNullOrUndefined(params as Record<string, any>) : undefined,
      }),
    }),

    createBankDetails: builder.mutation<BankDetailsItem, CreateBankDetailsRequest>({
      query: (bankData) => ({
        url: '/bank-details',
        method: 'POST',
        body: bankData,
      }),
      invalidatesTags: ['BankDetails'],
    }),
    downloadExcel: builder.mutation<Blob, DownloadExcelRequest>({
      query: (params) => ({
        url: '/bank-details/download-excel',
        method: 'GET',
        params,
        responseHandler: (response) => response.blob(),
      }),
      invalidatesTags: ['BankDetails'],
    }),
    updateBankDetails: builder.mutation<BankDetailsItem, UpdateBankDetailsRequest>({
      query: (bankData) => ({
        url: '/bank-details',
        method: 'PUT',
        body: bankData,
      }),
      invalidatesTags: ['BankDetails'],
    }),

    getBankDetailsHistory: builder.query<BankHistoryResponse, BankHistoryParams>({
      query: (params) => ({
        url: '/bank-details/history',
        params: params ? removeNullOrUndefined(params) : undefined,
      }),
      providesTags: ['BankDetails'],
    }),
  }),
});

export const {
  useGetBankDetailsQuery,
  useLazyGetBankDetailsQuery,
  useCreateBankDetailsMutation,
  useUpdateBankDetailsMutation,
  useGetBankDetailsHistoryQuery,
  useDownloadExcelMutation,
} = bankApi;
